import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { abs } from "@/lib/site";
import { CopyComboGrid } from "@/components/CopyComboGrid";
import { decorativeCollections, getDecorativeCollection } from "@/data/decorative";
import { COMBO_DESCRIPTIONS } from "@/data/decorative-descriptions";

export const Route = createFileRoute("/decorative/$slug")({
  loader: ({ params }) => {
    const col = getDecorativeCollection(params.slug);
    if (!col) throw notFound();
    return { col };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { col } = loaderData;
    const title = `${col.name} — copy & paste, ${col.combos.length} combos`;
    return {
      meta: [
        { title },
        { name: "description", content: col.tagline },
        { property: "og:title", content: col.name },
        { property: "og:description", content: col.tagline },
        { property: "og:url", content: abs(`/decorative/${params.slug}`) },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: abs(`/decorative/${params.slug}`) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: col.name,
            description: col.tagline,
            url: `/decorative/${params.slug}`,
            numberOfItems: col.combos.length,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: abs("/") },
              { "@type": "ListItem", position: 2, name: "Decorative", item: abs("/decorative") },
              { "@type": "ListItem", position: 3, name: col.name, item: abs(`/decorative/${params.slug}`) },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="flex flex-1 items-center justify-center p-12 text-center">
        <div>
          <h1 className="font-display text-3xl font-bold">Collection not found</h1>
          <Link to="/decorative" className="mt-4 inline-block text-accent hover:underline">
            ← Browse decorative symbols
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  ),
  component: DecorativePage,
});

// Standalone UI widgets (progress bars, transport controls) aren't text frames,
// so the "add your text" field doesn't apply to them.
const NO_TEXT_WRAP = new Set(["loading-bars", "music-players", "art-boxes"]);

function DecorativePage() {
  const { col } = Route.useLoaderData();
  const others = decorativeCollections.filter((c) => c.slug !== col.slug);
  const wrapText = !NO_TEXT_WRAP.has(col.slug);
  const noun = col.name.replace(/s$/, "").toLowerCase();

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto w-full max-w-7xl px-4 pb-6 pt-8 sm:px-6 sm:pt-16">
          <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/decorative" className="hover:text-foreground">Decorative</Link>
            <span>/</span>
            <span className="text-foreground">{col.name}</span>
          </nav>

          <h1 className="font-display text-3xl font-bold sm:text-5xl">{col.name}</h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">{col.tagline}</p>

          <div className="prose prose-invert mt-5 max-w-3xl space-y-4 text-foreground/90">
            {col.intro.map((p, i) => (
              <p key={i} className="text-[15px] leading-relaxed">{p}</p>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 pb-12 sm:px-6">
          <h2 className="mb-4 font-display text-xl font-semibold">
            {wrapText ? `Add your text to any ${noun}` : `Tap any ${col.name.toLowerCase()} to copy`}
          </h2>
          <CopyComboGrid combos={col.combos} wrapText={wrapText} wrapNoun={noun} descriptions={COMBO_DESCRIPTIONS[col.slug]} />
        </section>

        {others.length > 0 && (
          <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
            <h2 className="mb-4 font-display text-xl font-semibold">More decorative symbols</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((c) => (
                <Link
                  key={c.slug}
                  to="/decorative/$slug"
                  params={{ slug: c.slug }}
                  className="glass flex items-center gap-3 rounded-xl p-4 transition-colors hover:border-primary/40"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{c.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{c.tagline}</span>
                  </span>
                  <span className="text-muted-foreground">→</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
